import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Globe, CheckCircle2, Building2, Briefcase, Heart } from "lucide-react";
import { useLanguage } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="8" fill="#FFFFFF" />
      <circle cx="10" cy="10" r="2" fill="#F5B800" />
      <circle cx="26" cy="10" r="2" fill="#F5B800" />
      <circle cx="18" cy="18" r="2.5" fill="#F5B800" />
      <circle cx="10" cy="26" r="2" fill="#F5B800" />
      <circle cx="26" cy="26" r="2" fill="#F5B800" />
      <path d="M10 10 L18 18 L26 10 M10 26 L18 18 L26 26 M10 10 L10 26 M26 10 L26 26" stroke="#F5B800" strokeWidth="1" opacity="0.6" />
    </svg>
    <span className="font-display text-xl font-bold tracking-tight text-white">VF ACTIONS</span>
  </div>
);

const sectorIcons = { c1: Building2, c2: Briefcase, c3: Heart };

export default function ReferencesPage() {
  const { t, lang, toggleLanguage } = useLanguage();
  const caseKeys = ["c2", "c3", "c1"];

  return (
    <div className="min-h-screen bg-[#F6F4EC]" data-testid="references-page">
      {/* Hero band */}
      <header className="bg-[#0F4C4C] relative overflow-hidden">
        <div className="hero-network absolute inset-0 opacity-[0.08]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="references-logo-link"><Logo /></Link>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleLanguage} data-testid="references-language-switcher"
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-white/30 text-white hover:border-[#F5B800] hover:text-[#F5B800] transition-colors"
              >
                <Globe size={16} />
                <span className="font-semibold">{lang === 'fr' ? 'BR' : 'FR'}</span>
              </button>
              <Link to="/" data-testid="references-back-link" className="hidden sm:inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#F5B800] transition-colors">
                <ArrowLeft size={16} /> {t('references.back')}
              </Link>
            </div>
          </div>
          <div className="py-16 lg:py-24 max-w-3xl">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="chapter-kicker chapter-kicker--light">
              {t('references.kicker')}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }} className="font-display text-5xl sm:text-6xl font-bold text-white mt-5 mb-6">
              {t('references.title')}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: EASE }} className="text-lg text-white/80 leading-relaxed">
              {t('references.intro')}
            </motion.p>
          </div>
        </div>
      </header>

      {/* Case studies */}
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div data-testid="references-disclaimer" className="mb-10 bg-[#F5B800]/15 border-l-4 border-[#F5B800] px-5 py-4">
          <p className="text-sm text-[#142828] font-medium">{t('references.note')}</p>
        </div>
        <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-8 lg:space-y-10">
          {caseKeys.map((key, index) => {
            const IconComponent = sectorIcons[key];
            return (
              <motion.article key={key} variants={fadeUp} data-testid={`reference-card-${index + 1}`} className="card-editorial p-8 lg:p-12">
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <div className="w-11 h-11 bg-[#0F4C4C] flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-[#F5B800]" />
                  </div>
                  <span className="bg-[#F5B800] text-[#082A2A] text-xs font-bold uppercase tracking-wider px-3 py-1">{t(`references.cases.${key}.sector`)}</span>
                  <span data-testid={`reference-fictional-badge-${index + 1}`} className="text-[10px] font-bold uppercase tracking-wider text-[#516969] border border-[#DAD5C8] px-2 py-0.5">{t('references.fictional')}</span>
                  <span className="text-sm text-[#516969]">{t(`references.cases.${key}.location`)}</span>
                </div>

                <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#142828] mb-8">{t(`references.cases.${key}.title`)}</h2>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C49000] mb-2">{t('references.challenge')}</p>
                    <p className="text-sm text-[#516969] leading-relaxed">{t(`references.cases.${key}.challenge`)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0F4C4C] mb-2">{t('references.solution')}</p>
                    <p className="text-sm text-[#142828] leading-relaxed font-medium">{t(`references.cases.${key}.solution`)}</p>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#DAD5C8]">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#516969] mb-4">{t('references.results')}</p>
                  <div className="flex flex-wrap gap-3 mb-5">
                    {(t(`references.cases.${key}.results`) || []).map((r, i) => (
                      <span key={i} className="inline-flex items-center gap-2 bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-2 text-sm font-display font-bold text-[#0F4C4C]">
                        <CheckCircle2 className="w-4 h-4 text-[#C49000]" /> {r}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#516969]">
                    {t('references.platform')} : <span className="font-bold text-[#142828]">{t(`references.cases.${key}.platform`)}</span>
                  </p>
                </div>
              </motion.article>
            );
          })}
        </motion.div>

        <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-xs text-[#516969] italic mt-8">
          {t('references.note')}
        </motion.p>

        {/* CTA */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-16 bg-[#0F4C4C] frame-clip p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="hero-network absolute inset-0 opacity-[0.08]" />
          <div className="relative">
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-4">{t('references.ctaTitle')}</h2>
            <p className="text-white/75 mb-8 max-w-xl mx-auto">{t('references.ctaText')}</p>
            <a href="/#contact" data-testid="references-cta-btn" className="btn-accent px-8 py-4 font-bold text-sm inline-flex items-center gap-2 uppercase tracking-wider">
              {t('references.ctaBtn')} <ArrowRight size={16} />
            </a>
          </div>
        </motion.div>
      </main>

      <footer className="bg-[#082A2A] py-8">
        <p className="text-center text-xs text-white/50">© {new Date().getFullYear()} VF ACTIONS</p>
      </footer>
    </div>
  );
}
